/**
 * The day and the trend, as arithmetic.
 *
 * Everything on the attendance dashboard that can be wrong quietly lives here:
 * which calendar day a record belongs to, which bucket a record falls in, how
 * many days in a window actually carry data, and what the trend caption is
 * entitled to claim. None of it renders anything, so all of it can be tested.
 *
 * Two rules run through the whole file.
 *
 * 1. A calendar day is a UTC day string, "YYYY-MM-DD", on BOTH sides of every
 *    comparison. Comparing a Date to a Date has produced an off-by-one day here
 *    three separate times; the fix each time was to compare the strings, so the
 *    strings are the only representation this module admits.
 *
 * 2. Nothing is asserted that the data cannot support. A day with no records is
 *    `null`, never 0 — "we recorded nothing" and "nobody came in" are different
 *    claims and only one of them is in the data. The caption drops any clause it
 *    cannot compute rather than printing a plausible number.
 */

// toLocaleDateString with {month:"short"} renders "Sept" under current ICU,
// which is four letters and broke the labels. An explicit table, then.
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Any date-ish value → its UTC calendar day, or null if it isn't one. */
export const utcDayKey = (value) => {
  if (value == null) return null;
  if (typeof value === "string") {
    // Already a day string: take it as written. Re-parsing "2026-08-25" only
    // creates an opportunity to shift it.
    const m = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    if (m) return m[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

/** "2026-08-25" → "25 Aug". The axis and the caption both speak this. */
export const dayLabel = (key) => {
  const k = utcDayKey(key);
  if (!k) return "—";
  const [, month, day] = k.split("-");
  return `${Number(day)} ${MONTHS[Number(month) - 1]}`;
};

/** `key` moved by whole UTC days. Negative goes back. */
export const shiftDayKey = (key, deltaDays) => {
  const k = utcDayKey(key);
  if (!k) return null;
  const d = new Date(`${k}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + deltaDays);
  return d.toISOString().slice(0, 10);
};

/**
 * The window a range toggle means: `days` calendar days ENDING ON `endKey`,
 * inclusive. 7d ending 25 Aug is 19–25 Aug, not 18–25 — the end day is one of
 * the seven, and getting that wrong shifts every percentage by a day.
 */
export const rangeKeys = (endKey, days) => {
  const end = utcDayKey(endKey);
  const n = Math.max(1, Math.floor(days) || 0);
  if (!end) return [];
  const out = [];
  for (let back = n - 1; back >= 0; back -= 1) out.push(shiftDayKey(end, -back));
  return out;
};

// ---------------------------------------------------------------------------
// buckets
// ---------------------------------------------------------------------------

/**
 * The six counters, and what each one actually counts.
 *
 * `onTime` and `late` PARTITION the people who turned up; `missing` and
 * `overtime` are overlays on that same population, not slices of it. That is
 * why the six do not sum to the headcount and must not be drawn as if they do:
 * 109 on time + 15 late = 124 present, while the 6 missing punches and the 34
 * overtimes are drawn from those same 124.
 */
export const BUCKETS = ["present", "onTime", "late", "missing", "overtime", "absent"];

/**
 * A missing punch is a finished day with only one end of it.
 *
 * The server's own sweep never touches today (`processMissedCheckouts` cuts off
 * strictly before the current day), because somebody who clocked in an hour ago
 * and not out has not missed anything — they are at work. So today's open days
 * are "logged in", and only a past day, or a day the server has already flagged,
 * is a missing punch. Counting an in-progress day as missing would put six
 * people on a correction queue every single morning.
 */
export const isMissingPunch = (rec = {}, { isPast = false } = {}) => {
  if (rec?.missedCheckout === true) return true;
  const arrived = rec?.arrivalTime != null;
  const departed = rec?.departureTime != null;
  if (!arrived && !departed) return false;
  if (arrived && departed) return false;
  return isPast;
};

/** The flags one day-record carries. `dayKey` and `todayKey` are UTC day strings. */
export const flagsOf = (rec = {}, { dayKey, todayKey } = {}) => {
  const a = utcDayKey(dayKey);
  const t = utcDayKey(todayKey);
  const isPast = Boolean(a && t && a < t);
  const arrived = rec?.arrivalTime != null;
  const departed = rec?.departureTime != null;
  const missing = isMissingPunch(rec, { isPast });
  return {
    present: arrived || departed,
    arrived,
    departed,
    isPast,
    late: rec?.hasArrivedLate === true,
    onTime: arrived && rec?.hasArrivedLate !== true,
    missing,
    // "still at work" only exists on a day that is still running
    loggedIn: arrived && !departed && !missing,
    overtime: departed && Number(rec?.departureTimeDifference) < 0,
    corrected: rec?.isRegularized === true,
    lateMinutes: rec?.hasArrivedLate === true && Number(rec?.arrivalTimeDifference) > 0
      ? Math.round(Number(rec.arrivalTimeDifference))
      : null,
  };
};

/**
 * The one status a row shows, most-serious-first.
 *
 * Absent outranks everything because there is no day to describe. A missing
 * punch outranks lateness because it is unverified — you cannot argue about
 * whether someone was 22 minutes late on a day whose record is incomplete. The
 * label names WHICH end is missing, because "missing punch" alone sends the
 * reader back to the row to work out which column has the dash in it.
 */
export const rowStatus = (flags = {}, { absent = false } = {}) => {
  if (absent || !flags.present) return { key: "absent", label: "Absent", tone: "danger" };
  if (flags.missing) {
    return { key: "missing", label: flags.arrived ? "Missing out" : "Missing in", tone: "violet" };
  }
  if (flags.loggedIn) return { key: "loggedIn", label: "Logged in", tone: "info" };
  if (flags.late) {
    return {
      key: "late",
      label: flags.lateMinutes ? `Late ${flags.lateMinutes}m` : "Late",
      tone: "warning",
    };
  }
  return { key: "onTime", label: "On time", tone: "success" };
};

/**
 * The counters, from the decorated roster.
 *
 * `absent` is headcount minus the people with a record, floored at zero: a
 * roster that has shrunk since the day being viewed can otherwise produce a
 * negative absence, which is a number no one can act on.
 */
export const countBuckets = (entries = [], { headcount = 0 } = {}) => {
  const present = entries.filter((e) => e?.flags?.present);
  return {
    present: present.length,
    onTime: present.filter((e) => e.flags.onTime).length,
    late: present.filter((e) => e.flags.late).length,
    missing: present.filter((e) => e.flags.missing).length,
    overtime: present.filter((e) => e.flags.overtime).length,
    absent: Math.max((Number(headcount) || 0) - present.length, 0),
  };
};

/** The bucket each filter chip / counter selects. One predicate, two controls. */
export const BUCKET_PREDICATE = {
  all: () => true,
  present: (e) => Boolean(e?.flags?.present),
  onTime: (e) => Boolean(e?.flags?.onTime),
  late: (e) => Boolean(e?.flags?.late),
  missing: (e) => Boolean(e?.flags?.missing),
  overtime: (e) => Boolean(e?.flags?.overtime),
  absent: (e) => !e?.flags?.present,
};

/**
 * The roster the table shows: one bucket, then the search.
 *
 * The counter and the chip both call this with the same bucket key, which is
 * the whole reason the two cannot disagree — there is one filter, addressed
 * from two places, rather than two filters that have to be kept in step.
 */
export const selectRows = (entries = [], { bucket = "all", search = "" } = {}) => {
  const pick = BUCKET_PREDICATE[bucket] || BUCKET_PREDICATE.all;
  const list = entries.filter(pick);
  const q = String(search || "").trim().toLowerCase();
  if (!q) return list;
  return list.filter((e) => String(e?.name || "").toLowerCase().includes(q));
};

// ---------------------------------------------------------------------------
// the trend
// ---------------------------------------------------------------------------

/**
 * How much of a window was asserted rather than captured.
 *
 * A month where a tenth of the present days were typed in by a supervisor reads
 * identically to one where none were, and that difference is exactly what
 * someone auditing the register needs. Reported as a ratio, not a percentage,
 * so the caption can say "3 of 67" — a rate hides how small the sample is.
 */
export const punchAudit = (grouped = []) => {
  let corrected = 0;
  let total = 0;
  (grouped || []).forEach((day) => {
    (day?.employees || []).forEach((e) => {
      (e?.records || []).forEach((r) => {
        total += 1;
        if (r?.isRegularized) corrected += 1;
      });
    });
  });
  return { corrected, total };
};

/** The three offered series. At most two are ever drawn — see MAX_SERIES. */
export const SERIES_KEYS = ["attendance", "late", "missing"];

/**
 * Two lines, and no more.
 *
 * Three crossing lines on one 170px plot is a picture of nothing; the reader
 * can follow two and starts guessing at three. Picking a third drops the one
 * that has been shown longest, so the control always does something rather than
 * refusing — a toggle that silently ignores a click reads as broken.
 */
export const MAX_SERIES = 2;

export const toggleSeries = (selected = [], key) => {
  if (!SERIES_KEYS.includes(key)) return selected;
  if (selected.includes(key)) {
    // never leave the chart blank; the last line stays
    return selected.length > 1 ? selected.filter((k) => k !== key) : selected;
  }
  const next = [...selected, key];
  return next.slice(Math.max(0, next.length - MAX_SERIES));
};

/**
 * The window's series, day by day.
 *
 * `headcountOn(key)` is the denominator: the team as it stood on THAT day, so a
 * percentage from six weeks ago is not scored against people who had not joined
 * yet. Days with no records at all contribute `null` to every series and are
 * excluded from the average.
 */
export const buildTrend = ({
  grouped = [],
  endKey,
  rangeDays = 30,
  shift = "all",
  headcountOn = () => 0,
  todayKey,
} = {}) => {
  const keys = rangeKeys(endKey, rangeDays);
  if (!keys.length) return null;

  const byDate = {};
  (grouped || []).forEach((g) => {
    const k = utcDayKey(g?.date);
    if (k) byDate[k] = g;
  });
  const today = utcDayKey(todayKey);

  const points = keys.map((k) => ({ x: k, label: dayLabel(k) }));
  const attendance = [];
  const late = [];
  const missing = [];
  const arrival = [];
  let daysWithData = 0;

  keys.forEach((key) => {
    const employees = (byDate[key]?.employees || []).filter(
      (e) => shift === "all" || e?.records?.[0]?.shift === shift
    );
    if (!employees.length) {
      attendance.push(null);
      late.push(null);
      missing.push(null);
      arrival.push(null);
      return;
    }
    daysWithData += 1;
    const basis = Math.max(1, headcountOn(key) || 0);
    const isPast = Boolean(today && key < today);

    attendance.push((employees.length / basis) * 100);
    late.push(
      (employees.filter((e) => e?.records?.[0]?.hasArrivedLate === true).length / basis) * 100
    );
    // Today has no missing punches by construction (see isMissingPunch), so it
    // is a gap rather than a zero — plotting 0 would read as "resolved today".
    missing.push(
      isPast
        ? (employees.filter((e) => isMissingPunch(e?.records?.[0] || {}, { isPast: true })).length /
            basis) *
          100
        : null
    );

    const times = employees
      .map((e) => e?.records?.[0]?.arrivalTime)
      .filter((t) => t != null && !Number.isNaN(Number(t)))
      .map(Number);
    arrival.push(times.length ? times.reduce((s, t) => s + t, 0) / times.length : null);
  });

  const real = attendance.filter((v) => v !== null);
  const avg = real.length ? real.reduce((s, v) => s + v, 0) / real.length : null;
  const selectedValue = attendance[attendance.length - 1];

  return {
    keys,
    points,
    daysWithData,
    avg,
    selectedValue: selectedValue === undefined ? null : selectedValue,
    delta: selectedValue != null && avg != null ? selectedValue - avg : null,
    arrival,
    hasArrival: arrival.some((v) => v !== null),
    // The last day that actually has an arrival reading — the callout under the
    // arrival axis names it, so "08:54 on 25 Aug" is never a stale figure
    // labelled with the day the reader happens to be looking at.
    lastArrivalIndex: (() => {
      for (let i = arrival.length - 1; i >= 0; i -= 1) if (arrival[i] !== null) return i;
      return -1;
    })(),
    values: { attendance, late, missing },
  };
};

/**
 * The band the arrival chart is drawn in.
 *
 * Framed on the data and snapped out to quarter hours, with a 30-minute floor:
 * a team that all arrives within four minutes of each other would otherwise get
 * an axis so tight that ordinary noise draws as a mountain range.
 */
export const arrivalBand = (values = []) => {
  const vals = (values || []).filter((v) => v !== null && v !== undefined).map(Number);
  if (!vals.length) return { min: 480, max: 600 };
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const mid = (lo + hi) / 2;
  const half = Math.max(15, (hi - lo) / 2 + 10);
  return {
    min: Math.max(0, Math.floor((mid - half) / 15) * 15),
    max: Math.min(1440, Math.ceil((mid + half) / 15) * 15),
  };
};

/**
 * The caption, which states the chart's own limitations.
 *
 * Attendance data is always partly wrong — days nobody recorded, a headcount
 * reconstructed from joining dates that not everyone has, punches typed in
 * after the fact. A chart that hides that gets trusted more than it deserves,
 * so the caption carries it.
 *
 * Every clause is dropped rather than faked when its input is missing: no
 * joining dates means no "headcount approximate" claim either way, and no
 * punches at all means nothing to say about corrections. A caption of one
 * clause is the honest output for a window with nothing in it.
 */
export const trendCaption = ({
  rangeDays = 30,
  endKey,
  daysWithData = null,
  headcountApproximate = null,
  corrected = null,
  punches = null,
} = {}) => {
  const clauses = [`${rangeDays} days ending ${dayLabel(endKey)}`];
  if (daysWithData != null) clauses.push(`${daysWithData} with records`);
  // null means "we don't know whether it's exact", which is not a licence to
  // claim it is. Only an explicit true prints the warning.
  if (headcountApproximate === true) clauses.push("headcount approximate");
  if (punches != null && punches > 0 && corrected != null) {
    clauses.push(`${corrected} of ${punches} punches corrected`);
  }
  return clauses.join(" · ");
};

/** Minutes since midnight → "08:54". Used by the arrival axis and its callout. */
export const clockLabel = (minutes) => {
  if (minutes == null || Number.isNaN(Number(minutes))) return "—";
  const m = Math.round(Number(minutes));
  const h = Math.floor(m / 60);
  return `${String(h).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/**
 * Hours worked, as the table shows them.
 *
 * A day with only one punch has no duration — the record does not know when the
 * person left, and 8.0 from a shift length would be a number the device never
 * measured. It gets a dash, same as the punch it is missing.
 */
export const hoursLabel = (rec = {}) => {
  if (rec?.departureTime == null || rec?.arrivalTime == null) return "—";
  const mins = Number(rec?.totalHours);
  if (!Number.isFinite(mins) || mins <= 0) return "—";
  return (mins / 60).toFixed(1);
};
